import React, { useCallback, useMemo } from 'react';
import { di } from 'react-magnetic-di';
import type { EntryPointProps } from 'react-relay';
import Link from '@atlaskit/link';
import { isSafeUrl } from '@atlaskit/adf-schema';
import type { WithContextProps } from '@atlaskit/analytics-next';
import { Card, type CardProps } from '@atlaskit/smart-card';
import { fg } from '@atlassian/jira-feature-gating';
import prefixUrl from '@atlassian/jira-common-util-prefix-url/src/index.tsx';
import fireErrorAnalytics from '@atlassian/jira-errors-handling/src/utils/fire-error-analytics.tsx';

export type SmartLinkProps = {
	url: string;
} & CardProps &
	WithContextProps;

export const SmartLink = ({
	props: { url, ...cardProps },
}: EntryPointProps<{}, {}, SmartLinkProps, {}>) => {
	di(window);
	const prefixedUrl = useMemo(() => {
		const prefix = typeof window !== 'undefined' ? `${window.location.protocol}//` : '//';
		return prefixUrl(url, prefix);
	}, [url]);

	const handleClick = useCallback(
		(e: React.MouseEvent) => {
			try {
				if (typeof window !== 'undefined') {
					e.preventDefault();
					window.open(prefixedUrl, '_blank', 'noopener noreferrer');
				}
			} catch (error) {
				fireErrorAnalytics({
					error: new Error('Could not open an invalid link'),
					meta: {
						id: 'SmartLink',
						packageName: '@atlassian/jira-issue-field-smart-link',
						teamName: 'A4T-Pandora',
					},
				});
			}
		},
		[prefixedUrl],
	);

	const fallbackComponent = useCallback(
		() =>
			fg('replace-anchor-with-link__a4t-pandora') ? (
				<Link rel="noopener noreferrer" href={prefixedUrl} target="_blank">
					{url}
				</Link>
			) : (
				<a rel="noopener noreferrer" href={prefixedUrl} target="_blank">
					{url}
				</a>
			),
		[url, prefixedUrl],
	);

	if (!isSafeUrl(url)) {
		return url;
	}

	return (
		<Card
			url={prefixedUrl}
			fallbackComponent={fallbackComponent}
			showHoverPreview
			onClick={handleClick}
			analyticsContext={{ attributes: { fieldType: 'url' } }}
			{...cardProps}
		/>
	);
};
